import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import HeroSection from "./components/HeroSection";
import QuickActionCards from "./components/QuickActionCards";
import FeaturedPrograms from "./components/FeaturedPrograms";
import ServiceCharterSection from "./components/ServiceCharterSection";
import NoticeBar from "./components/NoticeBar";
import CTASection from "./components/CTASection";

const Homepage = () => {
  const [showPopup, setShowPopup] = useState(true);
  const [popupImage, setPopupImage] = useState(null);

  useEffect(() => {
    const img = new Image();
    img.src = "/assets/images/secondgrad.jpg";
    img.onload = () => setPopupImage(img.src);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          KARUMO TECHNICAL TRAINING INSTITUTE - Excellence in Technical
          Education
        </title>
        <meta
          name="description"
          content="KARUMO TECHNICAL TRAINING INSTITUTE (KTTI) offers comprehensive technical education with TVET CDACC accredited courses in ICT, Engineering, Business, and Hospitality. Transform your career with hands-on training in Meru, Kenya."
        />
        <meta
          name="keywords"
          content="KTTI, KARUMO TECHNICAL TRAINING INSTITUTE, technical training, TVET, CDACC, ICT courses, engineering courses, business courses, hospitality courses, Meru, Kenya, technical education, vocational training"
        />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://karumotti.ac.ke/" />
        <meta
          property="og:title"
          content="KARUMO TECHNICAL TRAINING INSTITUTE - Excellence in Technical Education"
        />
        <meta
          property="og:description"
          content="Transform your career with comprehensive technical education. TVET CDACC accredited courses in ICT, Engineering, Business, and Hospitality sectors."
        />
        <meta
          property="og:image"
          content="https://karumotti.ac.ke/kttilogo.png"
        />
        <meta
          property="og:site_name"
          content="KARUMO TECHNICAL TRAINING INSTITUTE"
        />
        <meta property="og:locale" content="en_KE" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://karumotti.ac.ke/" />
        <meta
          property="twitter:title"
          content="KARUMO TECHNICAL TRAINING INSTITUTE - Excellence in Technical Education"
        />
        <meta
          property="twitter:description"
          content="Transform your career with comprehensive technical education. TVET CDACC accredited courses in ICT, Engineering, Business, and Hospitality sectors."
        />
        <meta
          property="twitter:image"
          content="https://karumotti.ac.ke/kttilogo.png"
        />

        {/* Canonical URL */}
        <link rel="canonical" href="https://karumotti.ac.ke/" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "EducationalOrganization",
            name: "KARUMO TECHNICAL TRAINING INSTITUTE",
            alternateName: "KTTI",
            description:
              "KARUMO TECHNICAL TRAINING INSTITUTE offers comprehensive technical education with TVET CDACC accredited courses in ICT, Engineering, Business, and Hospitality sectors.",
            url: "https://karumotti.ac.ke",
            logo: "https://karumotti.ac.ke/kttilogo.png",
            address: {
              "@type": "PostalAddress",
              streetAddress: "3Km Off Kwa Amos Junction, Meru-Maua Road",
              addressLocality: "Kianjai",
              addressRegion: "Meru",
              postalCode: "60602",
              addressCountry: "KE",
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+254-705-636-443",
              email: "info@karumotti.ac.ke",
              contactType: "customer service",
              availableLanguage: "English",
            },
            sameAs: ["https://www.facebook.com/karumotti.ac.ke"],
            educationalCredentialAwarded: "TVET CDACC Certificate",
            hasEducationalUse: "Technical Education",
            knowsAbout: [
              "Information and Communication Technology",
              "Electrical Engineering",
              "Building Technology",
              "Business Administration",
              "Hospitality Management",
              "Agriculture",
            ],
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <NoticeBar />
        <main className="pt-[11rem] sm:pt-[11.5rem]">
         
          {/* Hero Section with Carousel */}
          <HeroSection />

          {/* Quick Action Cards for Different User Types */}
          <QuickActionCards />

          {/* Featured Programs Showcase */}
          <FeaturedPrograms />

          {/* Service Charter Section */}
          <ServiceCharterSection />

          {/* Call to Action with Application Deadline */}
          <CTASection />
        </main>
        {/* Footer */}
        <footer className="bg-blue-100 text-black py-12 ">
          <div className="container mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* About */}
              <div>
                <h3 className="text-lg font-bold mb-4">
                  KARUMO TECHNICAL TRAINING INSTITUTE
                </h3>
                <p className="text-gray-800 text-lg leading-relaxed">
                  Transforming careers through practical technical education.
                  Building industry-ready professionals for over 10 years.
                </p>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-semibold mb-4">Quick Links</h4>
                <ul className="space-y-2 text-m">
                  <li>
                    <a
                      href="/about"
                      className="text-black hover:text-blue-700 transition-colors"
                    >
                      About Us
                    </a>
                  </li>
                  <li>
                    <a
                      href="/courses"
                      className="text-black hover:text-blue-700 transition-colors"
                    >
                      Courses
                    </a>
                  </li>
                  <li>
                    <a
                      href="/departments"
                      className="text-black hover:text-blue-700 transition-colors"
                    >
                      Departments
                    </a>
                  </li>
                  <li>
                    <a
                      href="/resources"
                      className="text-black hover:text-blue-700 transition-colors"
                    >
                      Resources
                    </a>
                  </li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="font-semibold mb-4">Contact Info</h4>
                <ul className="space-y-2 text-l text-black">
                  <li>📍 KARUMO TECHNICAL TRAINING INSTITUTE</li>
                  <li>P.O BOX 276-60602 KIANJAI, MERU</li>
                  <li>📞 +254(0)705636443</li>
                  <li>✉️ info@karumotti.ac.ke</li>
                  <li>🕒 Mon-Fri: 8AM-5PM</li>
                </ul>
              </div>

              {/* Follow Us */}
              <div>
                <h4 className="font-semibold mb-4">Follow Us</h4>
                <div className="flex space-x-4">
                  <a
                    href="https://www.facebook.com/karumotti.ac.ke/"
                    target="_blank"
                    className="w-8 h-8 bg-yellow-300 rounded-full flex items-center justify-center hover:bg-primary/80 transition-colors"
                  >
                    <span className="text-lg">f</span>
                  </a>
                  <a
                    href="#"
                    className="w-8 h-8 bg-yellow-300 rounded-full flex items-center justify-center hover:bg-primary/80 transition-colors"
                  >
                    <span className="text-lg">x</span>
                  </a>
                  <a
                    href="#"
                    className="w-8 h-8 bg-yellow-300 rounded-full flex items-center justify-center hover:bg-primary/80 transition-colors"
                  >
                    <span className="text-lg">y</span>
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-900 mt-8 pt-8 text-center">
              <p className="text-gray-900 text-m">
                © {new Date()?.getFullYear()} KTTI. All rights reserved. |
                <a href="#" className="hover:text-black ml-1">
                  Privacy Policy
                </a>{" "}
                |
                <a href="#" className="hover:text-black ml-1">
                  Terms of Service
                </a>
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* Floating Image Popup - Bottom Right */}
      {showPopup && popupImage && (
        <div className="fixed bottom-6 right-6 z-50 max-w-xs sm:max-w-sm lg:max-w-md animate-fade-in">
          <div className="relative bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Close button */}
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-2 right-2 z-10 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg text-sm font-bold"
              aria-label="Close popup"
            >
              ✕
            </button>

            {/* Image */}
            <img
              src={popupImage}
              alt="2nd Graduation Ceremony & KARUMO@10"
              className="w-full h-auto object-cover"
            />

            {/* Caption overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary via-primary/80 to-transparent p-4 pt-12">
              {/* <p className="text-white text-sm sm:text-base font-bold text-center">
                🎓 2ND GRADUATION CEREMONY 2ND OCTOBER 2026
              </p> */}
              <p className="text-yellow-300 text-xs sm:text-sm text-center mt-1 font-semibold">
                🎉 Celebrating KARUMO@10
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Homepage;
